import { useEffect, useState } from 'react';

interface CountryOption {
  country: string;
  count: number;
}

const COUNTRY_FLAGS: Record<string, string> = {
  US: '\u{1F1FA}\u{1F1F8}',
  GB: '\u{1F1EC}\u{1F1E7}',
  AU: '\u{1F1E6}\u{1F1FA}',
  EU: '\u{1F1EA}\u{1F1FA}',
  IN: '\u{1F1EE}\u{1F1F3}',
  IE: '\u{1F1EE}\u{1F1EA}',
  NZ: '\u{1F1F3}\u{1F1FF}',
  SG: '\u{1F1F8}\u{1F1EC}',
};
const FALLBACK_FLAG = '\u{1F3F3}\u{FE0F}';

/**
 * Multi-select origin-country filter chip.
 *
 * Loads `/api/articles/countries` once on mount and renders one toggle per
 * distinct origin country with its article count. Selection state is owned
 * by the caller (controlled component) so it can serialise into URL params
 * for whichever list query is open.
 */
export function CountryFilter({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const [options, setOptions] = useState<CountryOption[]>([]);

  useEffect(() => {
    fetch('/api/articles/countries')
      .then((r) => (r.ok ? r.json() : []))
      .then((data: CountryOption[]) => setOptions(Array.isArray(data) ? data : []))
      .catch(() => setOptions([]));
  }, []);

  function toggle(country: string) {
    onChange(
      selected.includes(country)
        ? selected.filter((c) => c !== country)
        : [...selected, country],
    );
  }

  if (options.length === 0) return null;

  return (
    <div role="group" aria-label="Filter by origin country" className="flex flex-wrap gap-1">
      {options.map((opt) => {
        const isSel = selected.includes(opt.country);
        const flag = COUNTRY_FLAGS[opt.country] ?? FALLBACK_FLAG;
        return (
          <button
            key={opt.country}
            type="button"
            onClick={() => toggle(opt.country)}
            className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-xs ${
              isSel
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border bg-background text-muted-foreground hover:border-primary/50'
            }`}
          >
            <span aria-hidden="true">{flag}</span>
            <span>{opt.country}</span>
            <span className="text-[10px] tabular-nums opacity-70">{opt.count}</span>
          </button>
        );
      })}
    </div>
  );
}
